import { SessionData } from '../types'

const DEFAULT_TTL = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
const STORAGE_PREFIX = 'retention_'

interface StoredItem<T = any> {
  data: T
  createdAt: number
  expiresAt: number
}

export interface DataInfo {
  exists: boolean
  isExpired: boolean
  createdAt: Date | null
  expiresAt: Date | null
  timeToExpiry: number
}

export interface DataRetentionManager {
  storeData<T>(key: string, data: T, ttl?: number): void
  getData<T>(key: string): T | null
  removeData(key: string): void
  clearAllData(): void
  cleanupExpiredData(): number
  getDataInfo(key: string): DataInfo
  scheduleCleanup(): void
  stopCleanup(): void
}

export function createDataRetentionManager(): DataRetentionManager {
  let cleanupInterval: number | null = null

  function getStorageKey(key: string): string {
    return `${STORAGE_PREFIX}${key}`
  }

  function storeData<T>(key: string, data: T, ttl: number = DEFAULT_TTL): void {
    const now = Date.now()
    const item: StoredItem<T> = {
      data,
      createdAt: now,
      expiresAt: now + ttl
    }

    try {
      localStorage.setItem(getStorageKey(key), JSON.stringify(item))
    } catch (error) {
      console.warn('Failed to store data in localStorage:', error)
    }
  }

  function getData<T>(key: string): T | null {
    try {
      const stored = localStorage.getItem(getStorageKey(key))
      if (!stored) {
        return null
      }

      const item: StoredItem<T> = JSON.parse(stored)
      const now = Date.now()

      if (now > item.expiresAt) {
        // Data has expired, remove it
        removeData(key)
        return null
      }

      return item.data
    } catch (error) {
      console.warn('Failed to retrieve data from localStorage:', error)
      // Remove corrupted data
      removeData(key)
      return null
    }
  }

  function removeData(key: string): void {
    try {
      localStorage.removeItem(getStorageKey(key))
    } catch (error) {
      console.warn('Failed to remove data from localStorage:', error)
    }
  }

  function clearAllData(): void {
    const keysToRemove: string[] = []
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(STORAGE_PREFIX)) {
          keysToRemove.push(key)
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.warn('Failed to clear all data from localStorage:', error)
    }
  }

  function cleanupExpiredData(): number {
    const now = Date.now()
    let cleanedCount = 0
    const keysToRemove: string[] = []

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key || !key.startsWith(STORAGE_PREFIX)) {
          continue
        }

        const stored = localStorage.getItem(key)
        if (!stored) {
          continue
        }

        try {
          const item: StoredItem = JSON.parse(stored)
          if (now > item.expiresAt) {
            keysToRemove.push(key)
            cleanedCount++
          }
        } catch {
          // Remove corrupted entries
          keysToRemove.push(key)
          cleanedCount++
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.warn('Failed to cleanup expired data:', error)
    }

    return cleanedCount
  }

  function getDataInfo(key: string): DataInfo {
    try {
      const stored = localStorage.getItem(getStorageKey(key))
      if (!stored) {
        return {
          exists: false,
          isExpired: false,
          createdAt: null,
          expiresAt: null,
          timeToExpiry: 0
        }
      }

      const item: StoredItem = JSON.parse(stored)
      const now = Date.now()
      const isExpired = now > item.expiresAt

      return {
        exists: true,
        isExpired,
        createdAt: new Date(item.createdAt),
        expiresAt: new Date(item.expiresAt),
        timeToExpiry: Math.max(0, item.expiresAt - now)
      }
    } catch (error) {
      console.warn('Failed to get data info:', error)
      return {
        exists: false,
        isExpired: false,
        createdAt: null,
        expiresAt: null,
        timeToExpiry: 0
      }
    }
  }

  function scheduleCleanup(): void {
    if (cleanupInterval !== null) {
      return // Already scheduled
    }

    // Clean up every hour
    cleanupInterval = window.setInterval(() => {
      const cleaned = cleanupExpiredData()
      if (cleaned > 0) {
        console.log(`Auto-cleanup: removed ${cleaned} expired entries`)
      }
    }, 60 * 60 * 1000)

    // Also do an immediate cleanup
    cleanupExpiredData()
  }

  function stopCleanup(): void {
    if (cleanupInterval !== null) {
      clearInterval(cleanupInterval)
      cleanupInterval = null
    }
  }

  return {
    storeData,
    getData,
    removeData,
    clearAllData,
    cleanupExpiredData,
    getDataInfo,
    scheduleCleanup,
    stopCleanup
  }
}

// Default instance
export const defaultDataRetention = createDataRetentionManager()

// Auto-start cleanup on module load
defaultDataRetention.scheduleCleanup()

// Privacy-focused user session management
export function createUserSession(): SessionData {
  return {
    id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    startTime: Date.now(),
    lastActivity: Date.now(),
    promptCount: 0,
    educationProgress: [],
    safetyIncidents: 0
  }
}

export function updateUserSession(session: SessionData): SessionData {
  return {
    ...session,
    lastActivity: Date.now()
  }
}

// Session manager with automatic cleanup
export const sessionManager = {
  getCurrentSession(): SessionData | null {
    return defaultDataRetention.getData<SessionData>('current_session')
  },

  createSession(): SessionData {
    const session = createUserSession()
    defaultDataRetention.storeData('current_session', session)
    return session
  },

  updateSession(updates: Partial<SessionData>): SessionData | null {
    const current = this.getCurrentSession()
    if (!current) {
      return null
    }

    const updated = {
      ...current,
      ...updates,
      lastActivity: Date.now()
    }

    defaultDataRetention.storeData('current_session', updated)
    return updated
  },

  clearSession(): void {
    defaultDataRetention.removeData('current_session')
  },

  getSessionInfo(): DataInfo {
    return defaultDataRetention.getDataInfo('current_session')
  }
}